import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { CourseRepository } from '@/infrastructure/repositories/course.repository'
import { createApiClient } from '@/infrastructure/api/client'
import type { Course } from '@/domain/entities/course.entity'
import type { ContentLanguage } from '@/domain/entities/content-language.entity'
import type { PaginatedResult } from '@/domain/entities/base.entity'
import type { GetCoursesParams, GetContentLanguagesParams, GetPopularCoursesParams } from '@/domain/repositories/course.repository'
import { ErrorHandler } from '@/infrastructure/errors'
import { getLocaleEndonym } from '@/utils/locale'
import { useErrors } from '../composables/app/useErrors'

/**
 * Store для работы с курсами
 * Использует Vue 3 Composition API (setup синтаксис)
 */
export const useCourseStore = defineStore('course', () => {
  const { error, hasError, setApiError, clearErrors } = useErrors('course')
  // ========== State ==========
  const courses = ref<Course[]>([])
  const popularCourses = ref<Course[]>([])
  const pagination = ref<PaginatedResult<Course> | null>(null)
  const loading = ref(false)
  /** Параметри останнього запиту списку (для loadMore — щоб передавати course_category) */
  const lastFetchParams = ref<GetCoursesParams | null>(null)
  /** Доступные языки курсов для конкретной категории (загружаются на category page). */
  const allAvailableLocales = ref<ContentLanguage[]>([])
  /** Глобальный список языков курсов (без фильтра по категории) — для хедера. */
  const globalLocales = ref<ContentLanguage[]>([])
  const loadingLocales = ref(false)

  // ========== Repository ==========
  const runtimeConfig = useRuntimeConfig()
  const apiClient = createApiClient({
    baseUrl: runtimeConfig.public.NUXT_APP_LANDING_API,
  })
  const courseRepository = new CourseRepository(apiClient)

  // ========== Getters (Computed) ==========
  const hasCourses = computed(() => courses.value.length > 0)
  const isLoading = computed(() => loading.value)
  /** Уникальные языки из всех загруженных курсов + все доступные языки с количеством курсов */
  const availableLocales = computed(() => {
    const localeMap = new Map<string, ContentLanguage>()
    
    // Добавляем языки из загруженных курсов (без количества, т.к. его нет в курсе)
    courses.value.forEach((c) => {
      if (c.locale && !localeMap.has(c.locale)) {
        const fallbackLanguage: ContentLanguage = {
          id: c.locale,
          label: c.locale,
          endonym: getLocaleEndonym(c.locale),
          courseCount: 0,
          isDefault: false,
        }
        localeMap.set(c.locale, fallbackLanguage)
      }
    })
    // Добавляем все доступные языки с количеством курсов (загруженные заранее)
    allAvailableLocales.value.forEach((lang) => {
      localeMap.set(lang.id, lang)
    })
    
    return Array.from(localeMap.values()).sort((a, b) => a.id.localeCompare(b.id))
  })

  // ========== Actions ==========

  /**
   * Получить список курсов
   * @param params - Параметры запроса
   * @param signal - AbortSignal для отмены запроса
   * @param append - Если true, добавляет курсы к существующим (для пагинации)
   */
  async function fetchCourses(params?: GetCoursesParams, _signal?: AbortSignal, append: boolean = false) {
    loading.value = true
    clearErrors()

    try {
      const result = await courseRepository.findMany(params)
      if (!append && params) {
        lastFetchParams.value = { ...params }
      }
      // Если append = true и есть существующие курсы, добавляем новые
      if (append && courses.value.length > 0) {
        courses.value = [...courses.value, ...result.items]
      } else {
        // Иначе заменяем весь список
        courses.value = result.items
      }
      pagination.value = result
      return result
    } catch (err) {
      const handledError = ErrorHandler.handle(err)
      setApiError(ErrorHandler.toSerializable(handledError))
      throw handledError
    } finally {
      loading.value = false
    }
  }

  /**
   * Загрузить больше курсов (пагинация). Передає cursor з попередньої відповіді.
   */
  async function loadMore(limit: number = 12) {
    const hasNext = pagination.value?.hasNextPage
    if (!hasNext) {
      return
    }

    const cursor = pagination.value?.cursor
    if (!cursor) {
      return
    }

    const params: GetCoursesParams = {
      cursor,
      limit,
    }
    if (lastFetchParams.value?.course_category) {
      params.course_category = lastFetchParams.value.course_category
    }
    // Сохраняем фильтр по языку при пагинации
    if (lastFetchParams.value?.course_locale) {
      params.course_locale = lastFetchParams.value.course_locale
    }
    // Зберігаємо привязку до школи (account_courses), інакше пагінація покаже чужі курси
    if (lastFetchParams.value?.account_id) {
      params.account_id = lastFetchParams.value.account_id
    }
    // Сохраняем параметры сортировки при пагинации
    if (lastFetchParams.value?.sort_by) {
      params.sort_by = lastFetchParams.value.sort_by
    }
    if (lastFetchParams.value?.order) {
      params.order = lastFetchParams.value.order
    }

    try {
      await fetchCourses(params, undefined, true)
    } catch (err) {
      throw err
    }
  }

  /**
   * Очистить только список курсов категории (при переходе на другую категорию или открытии страницы)
   */
  function resetCategoryList() {
    courses.value = []
    pagination.value = null
    lastFetchParams.value = null
    allAvailableLocales.value = []
    clearErrors()
  }

  /**
   * Сбросить состояние store
   */
  function reset() {
    courses.value = []
    popularCourses.value = []
    pagination.value = null
    lastFetchParams.value = null
    loading.value = false
    clearErrors()
    allAvailableLocales.value = []
    globalLocales.value = []
  }

  /**
   * Очистить ошибку
   */
  function clearError() {
    clearErrors()
  }

  /**
   * Загрузить доступные языки курсов.
   *
   * Поведение зависит от наличия params.category:
   * - С category → обновляет allAvailableLocales (category-specific, для CourseLangSelector).
   *   Сторожевой флаг loadingLocales предотвращает параллельные запросы.
   * - Без category → обновляет globalLocales (глобальный список для хедера).
   *   Если globalLocales уже заполнен — пропускает (кеш на время жизни app).
   */
  async function fetchAllAvailableLocales(params?: GetContentLanguagesParams) {
    if (params?.category) {
      // Category-specific: не запускаем параллельные запросы
      if (loadingLocales.value) return
      loadingLocales.value = true
      try {
        const languages: ContentLanguage[] = await courseRepository.findContentLanguages(params)
        allAvailableLocales.value = languages
      } catch (err) {
        console.error('Failed to load available locales:', err)
        const handledError = ErrorHandler.handle(err)
        setApiError(ErrorHandler.toSerializable(handledError))
        allAvailableLocales.value = []
      } finally {
        loadingLocales.value = false
      }
    } else {
      // Global: кешируем на весь lifetime app (хедер не нуждается в обновлении)
      if (globalLocales.value.length > 0) return
      try {
        const languages: ContentLanguage[] = await courseRepository.findContentLanguages()
        globalLocales.value = languages
      } catch (err) {
        console.error('Failed to load global locales:', err)
        // Не обновляем error — это не критическая операция (хедер покажет fallback)
      }
    }
  }

  /**
   * Получить популярные курсы по языку
   * Использует бэкенд роут /courses/popular
   * @param params - Параметры запроса (locale, limit)
   */
  async function fetchPopularCourses(params: GetPopularCoursesParams, _signal?: AbortSignal) {
    if (!params.locale) throw new Error('Locale is required for popular courses')
    loading.value = true
    clearErrors()

    try {
      const result = await courseRepository.findPopularCourses(params)
      popularCourses.value = result
      return result
    } catch (err) {
      const handledError = ErrorHandler.handle(err)
      setApiError(ErrorHandler.toSerializable(handledError))
      throw handledError
    } finally {
      loading.value = false
    }
  }

  // ========== Return ==========
  return {
    // State
    courses,
    popularCourses,
    pagination,
    loading: isLoading,
    error,

    // Getters
    hasCourses,
    hasError,
    availableLocales,
    globalLocales,

    // Actions
    fetchCourses,
    loadMore,
    fetchAllAvailableLocales,
    fetchPopularCourses,
    reset,
    resetCategoryList,
    clearError,
  }
})

